/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Dialog, IconButton, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ReactMapboxGl, { Marker, Layer, Feature } from 'react-mapbox-gl';

import { JobContext } from 'context/jobContextContainer';
import { CallContext } from 'context/callContextContainer';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';
import {
  radiusApprovalService,
  radiusApprovalRemainingSec,
  formatMinSec,
  radiusMilestoneLabel,
  RADIUS_APPROVAL_ANSWER_GRACE_SEC,
  noteRadiusApprovalResolved,
  radiusApprovalKey,
} from 'services/radiusApprovalService';

import RedIcon from '../../images/marker_red.png';
// The driver app's own car marker (rndriver taxi-marker@2x, drawn at 30px).
import CarIcon from '../../images/car_marker.png';

const Map =
  typeof window !== 'undefined' &&
  ReactMapboxGl({
    accessToken: process.env.MAPBOX_ACCESS_TOKEN,
  });

const METERS_PER_DEG_LAT = 111320;

// Closed ring approximating a circle of radiusM meters around [lng, lat].
const circleRing = (lng, lat, radiusM, steps = 64) => {
  const dLat = radiusM / METERS_PER_DEG_LAT;
  const dLng = radiusM / (METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = (i / steps) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return ring;
};

const micro = value => parseFloat(value) / 1000000;

// mas_invoice_number is the invoice and leg run together; the server splits it
// (mas_invoice / mas_leg) when it knows how.
const masBrokerLabel = r =>
  r.mas_leg ? `MAS · invoice ${r.mas_invoice} · leg ${r.mas_leg}` : `MAS · ${r.mas_invoice || r.mas_invoice_number}`;

// Ticks on its own so the map isn't re-rendered (and re-fitted) every second;
// tells the dialog once time is up so Approve / Deny go away.
const Countdown = ({ request, onExpired }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const remaining = radiusApprovalRemainingSec(request, now);
  const answerable = remaining > RADIUS_APPROVAL_ANSWER_GRACE_SEC;
  useEffect(() => {
    if (!answerable && onExpired) onExpired();
  }, [answerable]);
  if (!answerable) {
    return (
      <div className="ra-note expired">
        <span className="ra-warn">!</span>
        <span>Too late to answer — the driver&apos;s wait is ending and they can proceed without approval.</span>
      </div>
    );
  }
  return (
    <div className="ra-note waiting">
      <span className="ra-clock">{formatMinSec(remaining)}</span>
      <span>
        {`If dispatch doesn't answer, the driver can proceed without approval in `}
        <strong>{formatMinSec(remaining)}</strong>
        .
      </span>
    </div>
  );
};

/**
 * Out-of-radius approval dialog. Shows the scheduled address with the base's
 * radius and the spot where the driver tapped Pick Up / Drop Off (with the
 * phone's GPS accuracy) — never the driver's live position: the decision is
 * about the tapped spot, which is what gets recorded. Approve / Deny is pushed
 * to the driver app; the first answer wins.
 */
const RadiusApprovalModal = ({ isCallCenter, callcenterActiveDb }) => {
  const {
    radiusApprovalModalInfo,
    setRadiusApprovalModalInfo,
    radiusApprovals,
    setRadiusApprovals,
  } = useContext(JobContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);
  const { basesSettings } = useContext(CallContext) || {};
  // Bumped by the countdown at zero — re-renders so Approve / Deny disappear.
  const [, setExpiredAt] = useState(0);

  const requests = Array.isArray(radiusApprovals) ? radiusApprovals : [];
  // Base + id: the call-center page lists several bases, each numbering from 1.
  const selectedId = radiusApprovalModalInfo ? radiusApprovalKey(radiusApprovalModalInfo) : null;
  // Live row (refreshed by the 15s poll); missing = no longer open.
  const live = selectedId ? requests.find(r => radiusApprovalKey(r) === selectedId) : null;
  const request = live || radiusApprovalModalInfo;

  const mapContent = useMemo(() => {
    if (!request) return null;
    const schedOk = !(parseInt(request.sched_lat, 10) === 0 && parseInt(request.sched_lon, 10) === 0);
    const tapOk = request.tap_lat != null && request.tap_lon != null && !(parseInt(request.tap_lat, 10) === 0 && parseInt(request.tap_lon, 10) === 0);
    if (!schedOk && !tapOk) return null;

    const sched = schedOk ? [micro(request.sched_lon), micro(request.sched_lat)] : null;
    const tap = tapOk ? [micro(request.tap_lon), micro(request.tap_lat)] : null;
    const radius = parseInt(request.radius_m, 10) || 250;
    const accuracy = parseInt(request.tap_accuracy_m, 10) || 0;

    const radiusRing = sched ? circleRing(sched[0], sched[1], radius) : null;
    const accuracyRing = tap && accuracy > 0 ? circleRing(tap[0], tap[1], accuracy) : null;

    // Fit the radius circle, the tapped spot and its accuracy circle.
    const points = [].concat(radiusRing || [], accuracyRing || [], tap ? [tap] : []);
    const lngs = points.map(p => p[0]);
    const lats = points.map(p => p[1]);
    const bounds = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    return (
      <div className="ra-map">
        <Map
          style="mapbox://styles/mapbox/streets-v9"
          fitBounds={bounds}
          // maxZoom: a single point (no scheduled point, no accuracy) would zoom to 22.
          // Extra top padding: the address pin stands 32px above its point and the legend is top-right.
          fitBoundsOptions={{ padding: { top: 72, bottom: 40, left: 40, right: 40 }, maxZoom: 17 }}
          containerStyle={{ height: '100%', width: '100%' }}
        >
          {radiusRing && (
            <Layer type="fill" paint={{ 'fill-color': '#2f6df6', 'fill-opacity': 0.12 }}>
              <Feature coordinates={[radiusRing]} />
            </Layer>
          )}
          {radiusRing && (
            <Layer type="line" paint={{ 'line-color': '#2f6df6', 'line-width': 2 }}>
              <Feature coordinates={radiusRing} />
            </Layer>
          )}
          {accuracyRing && (
            <Layer type="fill" paint={{ 'fill-color': '#5a6c7d', 'fill-opacity': 0.12 }}>
              <Feature coordinates={[accuracyRing]} />
            </Layer>
          )}
          {sched && tap && (
            <Layer type="line" paint={{ 'line-color': '#e07520', 'line-width': 2, 'line-dasharray': [2, 2] }}>
              <Feature coordinates={[sched, tap]} />
            </Layer>
          )}
          {sched && (
            <Marker coordinates={sched} anchor="bottom">
              <img src={RedIcon} alt="map-address" />
            </Marker>
          )}
          {tap && (
            <Marker coordinates={tap} anchor="center">
              <img src={CarIcon} alt="map-driver" width={30} height={30} />
            </Marker>
          )}
        </Map>
        <div className="ra-legend">
          {sched && (
            <>
              <span>
                <img alt="" src="https://maps.google.com/mapfiles/ms/icons/red.png" />
                Scheduled address
              </span>
              <span>
                <i className="ra-swatch radius" />
                {`${radius} m radius`}
              </span>
            </>
          )}
          {tap && (
            <span>
              <img alt="" src={CarIcon} />
              Where the driver tapped
            </span>
          )}
          {accuracyRing && (
            <span>
              <i className="ra-swatch accuracy" />
              GPS accuracy
            </span>
          )}
        </div>
      </div>
    );
  }, [selectedId]);

  if (!radiusApprovalModalInfo || !request) {
    return null;
  }

  const close = () => setRadiusApprovalModalInfo(null);

  const jobId = request.job_id;
  const label = radiusMilestoneLabel(request);
  const where = request.milestone === 'dropoff' ? 'drop-off' : 'pickup';
  const distance = request.distance_m == null ? null : parseInt(request.distance_m, 10);
  const radius = parseInt(request.radius_m, 10) || 250;
  const accuracy = request.tap_accuracy_m == null ? null : parseInt(request.tap_accuracy_m, 10);
  const tapKnown = request.tap_lat != null && request.tap_lon != null;
  const isOpen = !!live && radiusApprovalRemainingSec(live) > RADIUS_APPROVAL_ANSWER_GRACE_SEC;
  // Call center: answer in the base the request came from (ids repeat across bases).
  const requestDb = request.fetched_db || callcenterActiveDb;

  // Page through all open requests without closing the dialog.
  const answerable = list => list.filter(r => radiusApprovalRemainingSec(r) > RADIUS_APPROVAL_ANSWER_GRACE_SEC);
  const openRequests = answerable(requests);
  const foundIndex = openRequests.findIndex(r => radiusApprovalKey(r) === selectedId);
  const currentIndex = Math.max(0, foundIndex);
  const total = openRequests.length;
  // The shown request may have closed — the pager then still leads to the open ones.
  const showPager = foundIndex >= 0 ? total > 1 : total > 0;
  const goRequest = delta => {
    if (total === 0) return;
    if (foundIndex < 0) {
      setRadiusApprovalModalInfo(openRequests[delta > 0 ? 0 : total - 1]);
      return;
    }
    if (total <= 1) return;
    setRadiusApprovalModalInfo(openRequests[(((currentIndex + delta) % total) + total) % total]);
  };

  // The answer returns its base's fresh list — call center: merged with the
  // other bases' rows, which it doesn't cover.
  const withReceivedAt = list => {
    const receivedAt = Date.now();
    const fresh = (Array.isArray(list) ? list : []).map(r => ({ ...r, received_at: receivedAt, ...(isCallCenter && { fetched_db: requestDb }) }));
    if (!isCallCenter) return fresh;
    return [...requests.filter(r => r.fetched_db !== requestDb), ...fresh].sort(
      (a, b) => radiusApprovalRemainingSec(a) - radiusApprovalRemainingSec(b)
    );
  };

  const advanceOrClose = remaining => {
    setRadiusApprovals(remaining);
    const next = answerable(remaining);
    if (next.length > 0) {
      setRadiusApprovalModalInfo(next[Math.min(currentIndex, next.length - 1)]);
    } else {
      close();
    }
  };

  const onResolve = async decision => {
    showCircularProgress(decision === 'approve' ? 'Approving' : 'Denying');
    try {
      const res = await radiusApprovalService.resolve_radius_approval(request.id, decision, {
        ...(isCallCenter && { dbname: requestDb }),
      });
      hideCircularProgress();
      if (res && res.data && res.data.status === 'SUCCESS' && res.data.data && res.data.data.result !== 'error') {
        noteRadiusApprovalResolved();
        const { result, state, requests: fresh } = res.data.data || {};
        const freshList = withReceivedAt(fresh);
        if (result === 'ok') {
          showNotification(
            decision === 'approve'
              ? `Approved — Job #${jobId} driver can now proceed with ${label}.`
              : `Denied — Job #${jobId} driver was told to proceed inside the radius and try again.`,
            'success'
          );
        } else {
          const reasons = {
            approved: 'it was already approved by another dispatcher',
            denied: 'it was already denied by another dispatcher',
            cancelled: 'the driver cancelled the request or the trip changed',
            expired: 'time ran out — the driver can now proceed without approval',
          };
          showNotification(`Job #${jobId}: no change — ${reasons[state] || 'the request is no longer open'}.`, 'warning');
        }
        advanceOrClose(freshList);
      } else {
        showNotification('Sorry, failed to process — please retry.', 'error');
      }
    } catch (e) {
      hideCircularProgress();
      showNotification('Sorry, failed to process — please retry.', 'error');
    }
  };

  return (
    <StyledDialog open onClose={close} maxWidth="sm" fullWidth>
      <div className="ra-head">
        <div>
          <div className="ra-eyebrow">
            {`Out-of-radius ${label} · approval needed`}
            {request.fetched_db && basesSettings?.[request.fetched_db]?.base_name ? ` · ${basesSettings[request.fetched_db].base_name}` : ''}
          </div>
          <div className="ra-title">
            {`Job #${jobId}`}
            {request.passenger_name ? ` · ${request.passenger_name}` : ''}
          </div>
        </div>
        {showPager && (
          <div className="ra-pager">
            <button type="button" onClick={() => goRequest(-1)} aria-label="previous">‹</button>
            {foundIndex >= 0 ? `${currentIndex + 1} of ${total}` : `${total} open`}
            <button type="button" onClick={() => goRequest(1)} aria-label="next">›</button>
          </div>
        )}
        <IconButton size="small" onClick={close} style={{ color: '#8aa0b6' }} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {mapContent}

      <div className="ra-body">
        <dl className="ra-kv">
          <dt>Driver</dt>
          <dd>{[request.driver_name, request.vehicle_no ? `Car ${request.vehicle_no}` : null].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>Step</dt>
          <dd>{label}</dd>
          <dt>{request.milestone === 'dropoff' ? 'Drop-off' : 'Pickup'}</dt>
          <dd>{request.sched_addr || '—'}</dd>
          <dt>Distance</dt>
          <dd>
            {distance == null
              ? (tapKnown ? `Unknown — the ${where} address has no map location` : 'Unknown — the phone had no GPS fix')
              : `${distance} m from the ${where} address${distance > radius ? ` (${distance - radius} m outside the ${radius} m radius)` : ` (radius ${radius} m)`}`}
          </dd>
          {accuracy != null && accuracy > 0 && (
            <>
              <dt>GPS accuracy</dt>
              <dd>{`± ${accuracy} m`}</dd>
            </>
          )}
          {request.tap_time_display && (
            <>
              <dt>Tapped at</dt>
              <dd>{request.tap_time_display.trim()}</dd>
            </>
          )}
          {request.mas_invoice_number ? (
            <>
              <dt>Broker</dt>
              <dd>{masBrokerLabel(request)}</dd>
            </>
          ) : request.kinetik_trip_id ? (
            <>
              <dt>Broker</dt>
              <dd>Kinetik</dd>
            </>
          ) : null}
        </dl>

        {live ? (
          <Countdown request={live} onExpired={() => setExpiredAt(Date.now())} />
        ) : (
          <div className="ra-note expired">
            <span className="ra-warn">!</span>
            <span>This request is no longer open — it was answered, cancelled by the driver, or the time ran out.</span>
          </div>
        )}
      </div>

      <div className="ra-foot">
        {isOpen && (
          <>
            <Button variant="contained" onClick={() => onResolve('approve')} className="ra-btn-approve">
              Approve
            </Button>
            <Button onClick={() => onResolve('deny')} className="ra-btn-deny">
              Deny
            </Button>
          </>
        )}
        <Button onClick={close} className="ra-btn-ghost">
          Close
        </Button>
      </div>
    </StyledDialog>
  );
};

export default RadiusApprovalModal;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 12px;
    /* Scrolls on short screens (e.g. 1366x768) instead of squeezing the map. */
    overflow-x: hidden;
    overflow-y: auto;
  }

  .ra-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px 14px 20px;
    background: #0d1b2a;
    color: #e8eef5;

    > div:first-child {
      flex: 1;
    }
  }

  .ra-eyebrow {
    font-size: 11px;
    letter-spacing: 1.5px;
    font-weight: 700;
    color: #f5b301;
    text-transform: uppercase;
  }

  .ra-title {
    font-size: 16px;
    font-weight: 700;
  }

  .ra-pager {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #8aa0b6;
    font-size: 12.5px;
    white-space: nowrap;

    button {
      background: none;
      border: 1px solid #2a3a4d;
      color: #8aa0b6;
      border-radius: 5px;
      padding: 2px 8px;
      cursor: pointer;
    }
  }

  .ra-map {
    position: relative;
    flex: 0 0 auto;
    height: min(300px, 35vh);

    /* Top-right: the Mapbox logo and attribution sit along the bottom. */
    .ra-legend {
      position: absolute;
      top: 8px;
      right: 8px;
      max-width: calc(100% - 16px);
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 10px;
      padding: 5px 9px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.92);
      font-size: 11.5px;
      color: #33475b;

      span {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      img {
        width: 16px;
        height: 16px;
      }
    }

    .ra-swatch {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 50%;

      &.radius {
        background: rgba(47, 109, 246, 0.18);
        border: 2px solid #2f6df6;
      }

      &.accuracy {
        background: rgba(90, 108, 125, 0.25);
      }
    }
  }

  .ra-body {
    padding: 16px 20px 6px;
  }

  .ra-kv {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 5px 14px;
    font-size: 14px;
    margin: 0 0 14px;

    dt {
      color: #8a97a5;
    }
    dd {
      margin: 0;
    }
  }

  .ra-note {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 13.5px;
    margin-bottom: 12px;

    &.waiting {
      background: #fff8e1;
      border: 1px solid #f5d77a;
    }

    &.expired {
      background: #fdeaea;
      border: 1px solid #f0b6b6;
    }

    .ra-clock {
      font-size: 18px;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      color: #9a6b00;
    }

    .ra-warn {
      color: #c0392b;
      font-weight: 800;
    }
  }

  .ra-foot {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
    padding: 12px 20px 16px;
    border-top: 1px solid #e7ecf2;
    background: #f6f8fb;
  }

  .ra-btn-approve {
    background: #2e9e5b;
    color: #fff;

    &:hover {
      background: #258049;
    }
  }

  .ra-btn-deny {
    color: #c0392b;
    border: 1px solid #f0b6b6;
  }

  .ra-btn-ghost {
    color: #5a6c7d;
    border: 1px solid #d5dde6;
  }
`;
